import { ViewBooking } from "collins-js-sdk";
import { Phone, User } from "lucide-react";
import React, { useMemo } from "react";
import { EDSBadge } from "../../../../../../../components/eds/EDSBadge";
import { BookingLabelsEnum } from "../../../../../../../types/bookingEnums";
import { VenueGroupConfig } from "../../../../../../../types/VenueGroup";
import { VenueConfig } from "../../../../../../../types/venues";
import { getBookingAssignedAreaNamesAsString } from "../../../../../../../utils/singleBooking";
import {
  getUniqueZoneNames,
  renderTruncatedTables
} from "../utils/bookingCardTableAndZoneUtils";
import { useDiaryBookingCardColors } from "../hooks/useDiaryBookingCardColors";

interface DiaryBookingCardRunsheetDetailsRowProps {
  booking: ViewBooking;
  isOnRunSheet?: boolean;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
}

export const DiaryBookingCardRunsheetDetailsRow =
  React.memo<DiaryBookingCardRunsheetDetailsRowProps>(
    ({ booking, isOnRunSheet, venueConfig, venueGroupConfig }) => {
      // Use the colours hook to get label colours
      const {
        getBookingLabelColor,
        getCustomerLabelColor,
        allAvailableBookingLabels,
        allAvailableCustomerLabels
      } = useDiaryBookingCardColors({
        venueConfig,
        venueGroupConfig
      });

      // Compute table and zone string internally
      const tableWithZoneString = useMemo(() => {
        const tableNames = getBookingAssignedAreaNamesAsString(booking);

        const { zoneNamesFormatted } = getUniqueZoneNames(booking, venueConfig);

        if (!tableNames || tableNames === "Unassigned") {
          return tableNames;
        }

        const zoneDisplay =
          zoneNamesFormatted && zoneNamesFormatted.trim() !== ""
            ? zoneNamesFormatted
            : "No Zone";
        return `${tableNames} (${zoneDisplay})`;
      }, [booking.assignedAreas, booking.id, venueConfig?.bookable_area_zones]);

      if (!isOnRunSheet) {
        return null;
      }

      // Create labels array
      const allLabels: Array<{ id: string; label: string; colour: string }> =
        [];

      // Add booking labels
      if (booking.labels && Array.isArray(booking.labels)) {
        booking.labels.forEach((labelId: string) => {
          if (labelId === BookingLabelsEnum.LinkedBooking) {
            return;
          }

          const labelConfig = allAvailableBookingLabels.find(
            (l: any) => l.id === labelId
          );

          const colors = getBookingLabelColor(labelId);

          allLabels.push({
            id: labelId,
            label: labelConfig?.label ?? labelId.replace(/_/g, " "),
            colour: colors.backgroundColour.replace("#", "")
          });
        });
      }

      // Add customer labels
      if (booking.customer_labels && venueConfig && venueGroupConfig) {
        booking.customer_labels.forEach((labelId: string) => {
          const labelConfig = allAvailableCustomerLabels.find(
            (l: any) => l.id === labelId
          );

          const colors = getCustomerLabelColor(labelId);

          allLabels.push({
            id: `customer-${labelId}`,
            label: labelConfig?.label ?? labelId.replace(/_/g, " "),
            colour: colors.backgroundColour.replace("#", "")
          });
        });
      }

      const labelsToShow = allLabels;

      return (
        <div className="DiarySidebarListRow RunSheetDetailsRow">
          <div className="DiarySidebarListItem__detailsRow2">
            <div className="DiarySidebarListItem__leftContent">
              <span className="DiarySidebarListItem__tableInfo">
                {renderTruncatedTables(tableWithZoneString)}
              </span>
              <div className="DiarySidebarListItem__guestInfo">
                <User size={14} fill="none" strokeWidth={2} />
                <span className="DiarySidebarListItem__guestCount">
                  {booking.actualGuests ?? booking.numPeople ?? 0}
                </span>
              </div>
              {isOnRunSheet && booking.phone && (
                <div className="DiarySidebarListItem__phoneInfo">
                  <Phone size={14} fill="none" strokeWidth={2} />
                  <span className="DiarySidebarListItem__phoneNumber">
                    {booking.phone}
                  </span>
                </div>
              )}
            </div>

            {labelsToShow.length > 0 && (
              <div className="RunSheetDetailsRow__labels">
                {labelsToShow.map((label, index) => (
                  <EDSBadge
                    key={`label-${label.id}-${index}`}
                    variant="default"
                    className="RunSheetDetailsRow__label"
                    style={
                      {
                        "--booking-type-color": `#${label.colour}`,
                        "--booking-type-foreground": "var(--black)"
                      } as React.CSSProperties
                    }
                  >
                    <span className="RunSheetDetailsRow__labelText">
                      {label.label}
                    </span>
                  </EDSBadge>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }
  );

DiaryBookingCardRunsheetDetailsRow.displayName =
  "DiaryBookingCardRunsheetDetailsRow";
